import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  addCategory,
  deleteCategory,
  editCategory,
  fetchCategories,
} from "../Services/inventoryService"; //, updateCategory, deleteCategory
import Footer from "../Footer/Footer";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import { Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

type CategoryType = {
  id: string;
  label: string;
};

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [newCategory, setNewCategory] = useState<string>("");
  const [editingCategory, setEditingCategory] = useState<CategoryType | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState(false);

  const orgId = localStorage.getItem("orgId") || "";

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategories(orgId);

        const formattedOptions = categories.map((category: any) => ({
          id: category.categoryId,
          label: category.categoryName,
          createdBy: category.createdBy,
          createdDate: new Date(category.createdDate).toLocaleDateString(),
          updatedBy: category.updatedBy,
          updatedDate: new Date(category.updatedDate).toLocaleDateString(),
          status: category.status,
        }));
        setCategories(formattedOptions);
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };
    getCategories();
  }, []);

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      Swal.fire({
        title: "Error",
        text: "Please enter a category name.",
        icon: "error",
      });
      return;
    }
    try {
      const createdBy = "adminUser";
      const response = await addCategory(newCategory, orgId, createdBy);
      if (response.success) {
        const newCategoryData = {
          id: response.categoryId,
          label: newCategory,
          createdBy,
          createdDate: new Date().toLocaleDateString(),
          updatedBy: createdBy,
          updatedDate: new Date().toLocaleDateString(),
          status: "active",
        };
        setCategories([...categories, newCategoryData]);
        setNewCategory("");
        Swal.fire({
          title: "Success",
          text: "Category added successfully.",
          icon: "success",
        });
      } else {
        Swal.fire({
          title: "Error",
          text: response.message || "Failed to add category.",
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Error adding category. Please try again.",
        icon: "error",
      });
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory) return;
    try {
      await editCategory(
        editingCategory.id,
        editingCategory.label,
        orgId,
        "adminUser"
      );
      setCategories(
        categories.map((cat) =>
          cat.id === editingCategory.id ? editingCategory : cat
        )
      );
      setEditingCategory(null);
      setOpenDialog(false);
      Swal.fire("Updated!", "Category has been updated.", "success");
    } catch (error) {
      alert("Error updating category.");
    }
  };
  const handleDeleteCategory = async (categoryId: string) => {
    const confirmDelete = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (confirmDelete.isConfirmed) {
      try {
        await deleteCategory(categoryId, orgId, "adminUser"); //change updated user details
        setCategories(categories.filter((cat) => cat.id !== categoryId));
        Swal.fire("Deleted!", "Category has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error", "Error deleting category.", "error");
      }
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "label", headerName: "Category Name", width: 200, editable: true },
    { field: "status", headerName: "Status", width: 120 },
    { field: "createdBy", headerName: "Created By", width: 150 },
    { field: "createdDate", headerName: "Created Date", width: 150 },
    { field: "updatedBy", headerName: "Updated By", width: 150 },
    { field: "updatedDate", headerName: "Updated Date", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      
      renderCell: (params: any) => (
        <>
         <Tooltip title="Edit">
         <IconButton size="small" sx={{ padding: 0.5 }}
            onClick={() => {
              setEditingCategory(params.row);
              setOpenDialog(true);
            }}            
          ><EditIcon fontSize="small"  />
          </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
          <IconButton size="small" sx={{ padding: 0.5 }}
            color="error"
            onClick={() => handleDeleteCategory(params.row.id)}
            style={{ marginLeft: 8 }}
           
          >  <DeleteIcon  fontSize="small"/>
        </IconButton>
           </Tooltip>
        </>
      ),
    },
  ];
  return (
    <>
      {/* <div className="container"> */}
      <div className="page-location">
        <span>Master &gt; Category Master</span>
      </div>
      <Container maxWidth="lg" sx={{ padding: 4 }}>
        {/* <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
              color: "black",
              textTransform: "uppercase",
              textAlign: "center",
              marginBottom: 2,
              textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            }}
          >
            Category Management
          </Typography> */}

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={6}>
            <TextField
              label="New Category"
              fullWidth
              variant="outlined"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              required
              error={newCategory.trim() === ""}
              helperText={newCategory.trim() === "" ? "" : ""}
            />
          </Grid>
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              //startIcon={<AddIcon />}
              sx={{
                padding: "4px 8px",
                height: 55,
                width: 120,
                borderRadius: 1,
                backgroundColor: "#007BFF",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#0069d9",
                },
              }}
              onClick={handleAddCategory}
            >
              {/* Add Category */}
              Submit
            </Button>
          </Grid>
        </Grid>

        <DataGrid
          rows={categories}
          columns={columns}
          autoHeight
          pageSizeOptions={[5, 10]}
        />

        {/* Edit Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Edit Category</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              variant="outlined"
              value={editingCategory?.label || ""}
              onChange={(e) =>
                setEditingCategory({
                  ...editingCategory!,
                  label: e.target.value,
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditCategory}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      {/* </div> */}
      <Box
        sx={{
          position: "relative",
          left: 0,
          bottom: 0,
          width: "100%",
        }}
      >
        <Footer />
      </Box>
    </>
  );
};

export default CategoryManagement;
